/**
 * Room Type Utilities for Application Type Selection
 *
 * Infers room type from room name and maps to appropriate applicationType
 * for texture filtering queries.
 */

/**
 * Infer room type from room name.
 * Room names come from RoomPlan API or user input (e.g., "Master Bedroom", "Bathroom 1", "Kitchen").
 *
 * @param {string} roomName - The name of the room
 * @returns {string} The inferred room type
 */
export function inferRoomType(roomName) {
  if (!roomName) return 'Other';

  const name = roomName.toLowerCase();

  // Check for specific room types (order matters)
  // Check bathroom BEFORE shower - "Master Bathroom with Shower" should be Bathroom
  if (name.includes('bathroom') || name.includes('bath')) return 'Bathroom';
  if (name.includes('shower')) return 'Shower'; // Standalone shower rooms only
  if (name.includes('kitchen')) return 'Kitchen';
  if (name.includes('laundry')) return 'Laundry';
  if (name.includes('garage')) return 'Garage';
  if (name.includes('bedroom') || name.includes('nursery')) return 'Bedroom';
  if (name.includes('living') || name.includes('family')) return 'LivingRoom';
  if (name.includes('dining')) return 'DiningRoom';
  if (name.includes('office') || name.includes('study')) return 'Office';
  if (
    name.includes('hallway') ||
    name.includes('corridor') ||
    name.includes('entry')
  )
    return 'Hallway';
  if (
    name.includes('closet') ||
    name.includes('pantry') ||
    name.includes('storage')
  )
    return 'Storage';

  return 'Other';
}

/**
 * Get the appropriate applicationType for a surface based on room type and surface type.
 *
 * @param {string} roomType - The room type from inferRoomType()
 * @param {string} surfaceType - The surface type: 'floor' | 'wall' | 'ceiling' | 'backsplash' | 'countertop' | 'cabinet'
 * @returns {string} The applicationType for Firestore queries
 */
export function getApplicationType(roomType, surfaceType) {
  // Special surface types that don't depend on room
  if (surfaceType === 'countertop') return 'Countertop';
  if (surfaceType === 'cabinet') return 'Cabinet';

  // Shower wall — always ShowerWall regardless of room type
  if (surfaceType === 'showerWall') return 'ShowerWall';

  // Shower floor — always ShowerFloor regardless of room type
  if (surfaceType === 'showerFloor') return 'ShowerFloor';

  // Tub backsplash — always BathtubSurround regardless of room type
  if (surfaceType === 'tubBacksplash') return 'BathtubSurround';

  // Backsplash depends on room type — also includes Countertop textures
  if (surfaceType === 'backsplash') {
    if (roomType === 'Kitchen') return ['KitchenBacksplash', 'Countertop'];
    return ['BathroomBacksplash', 'Countertop'];
  }

  // Floor depends on room type
  if (surfaceType === 'floor') {
    switch (roomType) {
      case 'Shower':
        return 'ShowerFloor';
      case 'Bathroom':
        return 'BathroomFloor';
      case 'Kitchen':
        return 'KitchenFloor';
      case 'Laundry':
        return 'BathroomFloor'; // Similar water resistance needs
      default:
        return 'DryFloor';
    }
  }

  // Wall/ceiling depends on room type
  if (surfaceType === 'wall' || surfaceType === 'ceiling') {
    switch (roomType) {
      case 'Shower':
        return 'ShowerWall';
      case 'Bathroom':
        return 'BathroomWall';
      default:
        return 'DryWall';
    }
  }

  // Fallback
  return 'DryFloor';
}

/**
 * Get all valid application types.
 * Useful for UI dropdowns and validation.
 *
 * @returns {Array<{value: string, label: string, description: string}>}
 */
export function getApplicationTypeOptions() {
  return [
    {
      value: 'DryFloor',
      label: 'Dry Floor',
      description: 'Living room, bedroom, hallway floors',
    },
    {
      value: 'BathroomFloor',
      label: 'Bathroom Floor',
      description: 'Water-resistant bathroom floors',
    },
    {
      value: 'KitchenFloor',
      label: 'Kitchen Floor',
      description: 'Durable, stain-resistant kitchen floors',
    },
    {
      value: 'ShowerFloor',
      label: 'Shower Floor',
      description: 'Slip-resistant shower floors (small tiles)',
    },
    {
      value: 'DryWall',
      label: 'Dry Wall',
      description: 'Living room, bedroom, hallway walls',
    },
    {
      value: 'BathroomWall',
      label: 'Bathroom Wall',
      description: 'Moisture-resistant bathroom walls',
    },
    {
      value: 'ShowerWall',
      label: 'Shower Wall',
      description: 'Waterproof shower walls',
    },
    {
      value: 'KitchenBacksplash',
      label: 'Kitchen Backsplash',
      description: 'Heat/grease resistant kitchen backsplash',
    },
    {
      value: 'BathroomBacksplash',
      label: 'Bathroom Backsplash',
      description: 'Moisture-resistant bathroom backsplash',
    },
    {
      value: 'BathtubSurround',
      label: 'Bathtub Surround',
      description: 'Bathtub surround wall surfaces',
    },
    {
      value: 'Countertop',
      label: 'Countertop',
      description: 'Countertop surfaces',
    },
    { value: 'Cabinet', label: 'Cabinet', description: 'Cabinet surfaces' },
  ];
}
